import { useId, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import type { SessionDirectoryResult } from '@borgee/plugin-sdk/session-directory';
import './SessionDirectory.css';

interface Props {
  status: 'idle' | 'loading' | 'unsupported' | 'ready' | 'error';
  result?: SessionDirectoryResult;
  selectedBinding?: string;
  selectedNativeId?: string;
  onSelect(bindingId: string): void;
  onRefresh(): void;
}

export default function SessionDirectory({ status, result, selectedBinding, selectedNativeId, onSelect, onRefresh }: Props) {
  const { t } = useLingui();
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filterId = useId();
  const filterButton = useRef<HTMLButtonElement>(null);
  if (status === 'idle' || status === 'unsupported') return null;
  const entries = status === 'ready' ? result?.sessions ?? [] : [];
  const query = searchOpen ? search.trim().toLowerCase() : '';
  const matching = entries.filter(entry => entry.native_session_id.toLowerCase().includes(query));
  const selected = entries.find(entry => entry.binding_id === selectedBinding);
  const selectedVisible = matching.some(entry => entry.binding_id === selectedBinding);
  const loading = status === 'loading';
  return <section className="session-directory" aria-label={t`Sessions in this direct message`}>
    <div className="session-directory-heading">
      <span>{t`Opened sessions`}</span>
      <div className="session-directory-actions">
        <button ref={filterButton} type="button" aria-expanded={searchOpen} aria-controls={filterId} onClick={() => setSearchOpen(value => !value)}>{t`Filter`}</button>
        <button type="button" disabled={loading} onClick={onRefresh} aria-label={status === 'error' ? t`Retry sessions` : t`Refresh sessions`} title={t`Refresh sessions`}>
          {status === 'error' ? t`Retry sessions` : <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d="M20 7v5h-5M4 17v-5h5" /><path d="M6 7a7 7 0 0 1 12-1l2 6M4 12l2 6a7 7 0 0 0 12-1" /></svg>}
        </button>
      </div>
    </div>
    {searchOpen && <label className="session-directory-filter" id={filterId}>
      <span>{t`Filter loaded sessions`}</span>
      <input type="search" value={search} autoFocus placeholder={t`Session ID`} onChange={event => setSearch(event.target.value)} onKeyDown={event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setSearchOpen(false); filterButton.current?.focus(); }
      }} />
    </label>}
    {loading && <p role="status">{t`Loading sessions…`}</p>}
    {status === 'error' && <p role="status">{t`Sessions could not be loaded.`}</p>}
    {status === 'ready' && <>
      {(entries.length > 0 || !!selectedBinding) && <select className="dsh-session-selector" aria-label={t`Session`} value={selectedBinding ?? ''} onChange={event => onSelect(event.target.value)}>
        {(!selectedBinding || !selectedVisible) && <option value={selectedBinding ?? ''} disabled>{selected?.native_session_id ?? selectedNativeId ?? (selectedBinding ? t`Selected session` : t`Select a session`)}{selectedBinding ? t` (current)` : ''}{selected?.is_default ? t` (conversation default)` : ''}</option>}
        {matching.map(entry => <option key={entry.binding_id} value={entry.binding_id}>{entry.native_session_id}{entry.binding_id === selectedBinding ? t` (current)` : ''}{entry.is_default ? t` (conversation default)` : ''}</option>)}
      </select>}
      {query && !matching.length && <p role="status">{t`No sessions match this filter.`}</p>}
      {result?.complete && !entries.length && !selectedBinding && <p role="status">{t`No registered sessions. Send a message to start a session.`}</p>}
      {result && !result.complete && <p role="status">{t`Only part of the session list is available.`}</p>}
    </>}
  </section>;
}
